import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Award, ShieldCheck } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

const PRINCIPLES = [
  {
    n: "01",
    title: "Real design work, not templates",
    body: "Every logo, every website, every invoice layout is drawn for the individual trade business. If it looks the same as someone else's, we've missed.",
  },
  {
    n: "02",
    title: "One clear price",
    body: "No upfront design fee, no hidden hosting bill, no per-user charge. One flat monthly payment for everything.",
  },
  {
    n: "03",
    title: "You keep what's yours",
    body: "Your domain is registered in your business's name. Your customer list, your invoices, your content — all yours to take with you if you ever leave.",
  },
  {
    n: "04",
    title: "Talk to a person",
    body: "You'll speak directly to Elliot on every project. No offshore team, no support-ticket queue.",
  },
];

const AboutPage = () => (
  <div className="min-h-screen bg-[color:var(--bg)] overflow-x-hidden" data-testid="about-page">
    <SiteHeader />

    {/* Header */}
    <section className="pt-28 sm:pt-32 lg:pt-40 pb-16 lg:pb-24 border-b border-[color:var(--border-soft)]">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 items-baseline mb-8">
          <div className="col-span-6"><span className="grid-ref">§ About / ReadyTrades</span></div>
          <div className="col-span-6 flex justify-end"><span className="stamp stamp-ink">Est. 2024 · Essex</span></div>
        </div>
        <h1 className="h-xl text-[color:var(--ink)] max-w-4xl">
          A design studio for <span className="font-serif-italic">the people</span> who actually build things.
        </h1>
        <p className="copy-lg text-[color:var(--muted)] mt-8 max-w-2xl">
          ReadyTrades is a small UK studio giving independent tradespeople the kind of branding, websites and business tools normally reserved for much larger companies — for one flat monthly payment.
        </p>
      </div>
    </section>

    {/* Founder */}
    <section className="py-20 lg:py-32 border-b border-[color:var(--border-soft)]">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-8 lg:gap-16 items-start">
          <div className="col-span-12 md:col-span-5">
            <div className="aspect-[4/5] bg-[color:var(--bg-alt)] border border-[color:var(--border-soft)] rounded-sm relative overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--border-soft)] bg-[color:var(--bg-paper)] relative z-10">
                <span className="grid-ref">FIG. 01 · Founder</span>
                <span className="stamp stamp-ink">Elliot</span>
              </div>
              <img
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_888d19bb-6d1f-4406-ae28-13dde7109b69/artifacts/8gi82110_ChatGPT%20Image%20Aug%2018%2C%202026%2C%2005_40_16%20PM.png"
                alt="Elliot, founder of ReadyTrades"
                loading="lazy"
                className="flex-1 w-full object-cover min-h-0"
                style={{ transform: 'scale(1.18)', transformOrigin: 'center 90%' }}
              />
              <div className="px-4 py-3 border-t border-[color:var(--border-soft)] bg-[color:var(--bg-paper)] flex items-center justify-between relative z-10">
                <span className="text-[12px] font-medium text-[color:var(--ink)]">Elliot · Founder</span>
                <span className="grid-ref">4:5 · Portrait</span>
              </div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-7">
            <span className="grid-ref">The person behind it</span>
            <h2 className="h-lg text-[color:var(--ink)] mt-3">
              <span className="font-serif-italic">"I wanted</span> tradespeople to have the kind of brand normally reserved for far bigger businesses."
            </h2>
            <div className="mt-8 space-y-4 copy-lg text-[color:var(--muted)] max-w-xl">
              <p>
                ReadyTrades is run by Elliot — a UK-based designer with a decade of branding, website and digital work behind him.
              </p>
              <p>
                After working with consumer brands and early-stage startups, one thing became obvious: the tradespeople doing the actual work in this country were the least well-served by the design industry. Websites cost thousands. Agencies wouldn't return your call unless you had a marketing manager to speak to first.
              </p>
              <p>
                ReadyTrades exists to close that gap. Same design quality, one straightforward monthly payment, and a real person on the other end of the phone.
              </p>
            </div>
            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <span className="stamp stamp-ink"><MapPin size={11} /> Great Dunmow · Essex</span>
              <span className="stamp stamp-ink">10+ years in design</span>
              <span className="stamp stamp-accent">Sole studio</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Principles */}
    <section className="py-20 lg:py-32 border-b border-[color:var(--border-soft)]">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 items-end mb-14">
          <div className="col-span-12 lg:col-span-7">
            <span className="grid-ref">§ How we work</span>
            <h2 className="h-lg text-[color:var(--ink)] mt-3">
              Four things we're <span className="font-serif-italic">strict</span> about.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <p className="copy-lg text-[color:var(--muted)] max-w-md">
              We could pretend to have twenty values. In practice, only four ever come up in conversation. So we've made them the promise.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {PRINCIPLES.map((p) => (
            <article key={p.n} className="border-t-2 border-[color:var(--ink)] pt-6">
              <div className="flex items-center gap-4 mb-4">
                <span className="font-display tabular text-[color:var(--accent)] text-[15px]">{p.n}</span>
                <span className="grid-ref">Principle</span>
              </div>
              <h3 className="font-display text-[22px] lg:text-[26px] text-[color:var(--ink)] leading-tight">{p.title}</h3>
              <p className="copy-md text-[color:var(--muted)] mt-3 max-w-md">{p.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>

    {/* Reassurance strip */}
    <section className="py-16 lg:py-24 bg-[color:var(--bg-alt)] border-b border-[color:var(--border-soft)]">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, title: "Nothing to build yourself", body: "We design, build and connect everything. You approve it and go live." },
            { icon: Award, title: "10+ years of design work", body: "A decade helping brands look and read like themselves. Now for tradespeople." },
            { icon: MapPin, title: "Small, UK-based studio", body: "One founder, one designer, direct conversations. No middlemen." },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-sm border border-[color:var(--ink)] flex items-center justify-center flex-shrink-0">
                <Icon size={16} strokeWidth={1.5} className="text-[color:var(--ink)]" />
              </span>
              <div>
                <p className="font-display text-[18px] text-[color:var(--ink)]">{title}</p>
                <p className="text-[14px] text-[color:var(--muted)] mt-1.5 leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-20 lg:py-32">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-8">
            <span className="grid-ref">§ Get started</span>
            <h2 className="h-lg text-[color:var(--ink)] mt-3 max-w-2xl">
              Fancy a chat? <span className="font-serif-italic">No obligation.</span>
            </h2>
            <p className="copy-lg text-[color:var(--muted)] mt-5 max-w-xl">
              Tell us about your trade business and we'll design a free preview of your new website — you decide what to do next.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-start lg:justify-end">
            <Link to="/get-started" className="btn-primary" data-testid="about-cta-primary">Get My Free Preview <ArrowRight size={15} /></Link>
            <a href="mailto:contact@readytrades.co.uk" className="btn-secondary">Talk to Elliot</a>
          </div>
        </div>
      </div>
    </section>

    <SiteFooter />
  </div>
);

export default AboutPage;
